import { Option } from 'effect'
import React from 'react'
import { styled } from 'styled-components'

import {
	Body,
	Grid,
	InfoBulle,
	SmallBody,
	TitreObjectif,
} from '@/design-system'
import { Montant, montantToString } from '@/domaine/Montant'
import { useInitialRender } from '@/hooks/useInitialRender'

import { Appear } from '../ui/animate'
import AnimatedTargetValue from '../ui/AnimatedTargetValue'

export type ObjectifDeSimulationProps = {
	id: string
	titre: React.ReactNode
	sousTitre?: React.ReactNode
	description?: React.ReactNode
	valeur: Option.Option<Montant> | string
	small?: boolean
	appear?: boolean
	isInfoMode?: boolean
	displayedUnit?: string
}

export function ObjectifDeSimulation({
	id,
	titre,
	sousTitre,
	description,
	valeur,
	displayedUnit,
	small = false,
	appear = true,
}: ObjectifDeSimulationProps) {
	const initialRender = useInitialRender()

	const valeurAffichee =
		typeof valeur === 'string'
			? valeur
			: Option.match(valeur, {
					onNone: () => '—',
					onSome: (montant) => montantToString(montant, displayedUnit),
				})

	return (
		<Appear unless={!appear || initialRender}>
			<StyledGoal $small={small}>
				<GridCentered container spacing={2}>
					<Grid item md="auto" sm={small ? 9 : 8} xs={8}>
						<TitreObjectif id={`${id}-label`}>{titre}</TitreObjectif>

						{sousTitre && (
							<SousTitre id={`${id}-description`}>{sousTitre}</SousTitre>
						)}

						{description && <InfoBulle description={description} />}
					</Grid>
					<Grid item>
						{!small && typeof valeur !== 'string' && Option.isSome(valeur) && (
							<AnimatedTargetValue value={valeur.value} />
						)}
						<StyledValue
							id={`${id}-value`}
							aria-labelledby={`${id}-label`}
							aria-describedby={sousTitre ? `${id}-description` : undefined}
						>
							{valeurAffichee}
						</StyledValue>
					</Grid>
				</GridCentered>
			</StyledGoal>
		</Appear>
	)
}

const GridCentered = styled(Grid)`
	display: grid;
	grid-template-columns: 1.15fr 1fr;
	gap: ${({ theme }) => theme.spacings.md};
	align-items: baseline;
	justify-content: space-between;

	& > div {
		padding: 0;
		max-width: 100%;
		text-align: right;
	}

	@media (max-width: ${({ theme }) => theme.breakpointsWidth.sm}) {
		grid-template-columns: 1fr;
		gap: ${({ theme }) => theme.spacings.xs};

		& > div {
			text-align: left;
		}
	}
`

const StyledGoal = styled.div<{ $small: boolean }>`
	position: relative;
	z-index: 1;
	padding: ${({ theme, $small }) => theme.spacings[$small ? 'xxs' : 'sm']} 0;

	@media print {
		padding: 0;
	}
`

const SousTitre = styled(SmallBody)`
	margin: 0;
	white-space: pre-line;
`

const StyledValue = styled(Body)`
	margin: 1.2rem 0;
	font-size: ${({ theme }) => theme.fontSizes.lg};
	text-align: right;
	padding-right: ${({ theme }) => theme.spacings.sm};
`
